import React from 'react';

import {
  faFilePdf
} from '@fortawesome/free-solid-svg-icons';
import {
  FontAwesomeIcon
} from '@fortawesome/react-fontawesome';

import {
  jsPDF
} from 'jspdf';

import {
  useTranslation
} from 'react-i18next';

import Logger from '@terrestris/base-util/dist/Logger';

import RGPrintButton, {
  PrintButtonProps as RGPrintButtonProps
} from '@terrestris/react-geo/dist/Button/PrintButton/PrintButton';
import {
  useMap
} from '@terrestris/react-geo/dist/Hook/useMap';
import {
  isWmsLayer
} from '@terrestris/react-geo/dist/Util/typeUtils';

import {
  useAppDispatch
} from '../../hooks/useAppDispatch';

import {
  setIsLoading
} from '../../store/isLoading';

import DahrendorfBold from './Dahrendorf-bold';
import Dahrendorf from './Dahrendorf-normal';

export const PrintButton: React.FC<Partial<RGPrintButtonProps>> = ({
  ...passThroughProps
}): JSX.Element => {

  const dispatch = useAppDispatch();

  const map = useMap();

  const {
    t
  } = useTranslation();

  const onProgressChange = () => {
    dispatch(setIsLoading(true));
  };

  const pdfPrintFunc = async (
    mapImgUrl: string,
    pdfSpec: any,
    title: string,
    legendTitle: string
  ) => {

    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      compress: true,
      format: 'a4', // 210 by 297mm
      putOnlyUsedFonts: true
    });

    // Add the custom fonts.
    doc.addFileToVFS('Dahrendorf.ttf', Dahrendorf);
    doc.addFont('Dahrendorf.ttf', 'Dahrendorf', 'normal');

    doc.addFileToVFS('Dahrendorf-Bold.ttf', DahrendorfBold);
    doc.addFont('Dahrendorf-Bold.ttf', 'Dahrendorf', 'bold');

    const mapWidth = pdfSpec.size[0];
    const mapHeight = pdfSpec.size[1];

    // Add the map.
    doc.addImage(mapImgUrl, 'PNG', 10, 17, mapWidth, mapHeight);

    // Add the title.
    doc.setFont('Dahrendorf', 'bold');
    doc.setFontSize(20);
    doc.text(title, doc.internal.pageSize.width / 2, 13, {
      align: 'center'
    });

    // Add the attribution.
    doc.setFont('Dahrendorf', 'normal');
    doc.setFontSize(9);
    const attributionLines = doc.splitTextToSize(getAttributionsText(pdfSpec), doc.internal.pageSize.width - 20);
    doc.text(attributionLines, doc.internal.pageSize.width - 10, 270, {
      align: 'right'
    });

    pdfSpec.layers.forEach((printLayer: any) => {
      const layer = map?.getAllLayers().find(l => l.get('name') === printLayer.layerName);

      if (layer && isWmsLayer(layer) && layer.get('legendVisible')) {
        printLayer.legendUrl = `${layer.getSource()?.getLegendUrl()}&` +
          'LEGEND_OPTIONS=fontAntiAliasing:true;forceLabels:on&TRANSPARENT=true';
      }
    });

    // Add the legend page (if needed).
    if (pdfSpec.layers?.some((l: any) => l.legendUrl && l.legendUrl.length > 0)) {
      // Add the second page for legends.
      doc.addPage('a4', 'p');

      // Add the legend title.
      doc.setFont('Dahrendorf', 'bold');
      doc.setFontSize(20);
      doc.text(legendTitle, doc.internal.pageSize.width / 2, 13, {
        align: 'center'
      });

      let xPosition = 20;

      for (let i = 0; i < pdfSpec.layers?.length; i++) {
        const layer: any = pdfSpec.layers?.[i];
        const legendUrl = layer.legendUrl;
        const name = layer.layerName;

        if (!(legendUrl && name)) {
          continue;
        }

        try {
          // Determine the legend image dimensions.
          const response = await fetch(legendUrl);
          const blob = await response.blob();
          const base64 = URL.createObjectURL(blob);
          let img: HTMLImageElement = new Image();
          img.src = base64.toString();
          await img.decode();

          doc.setFont('Dahrendorf', 'bold');
          doc.setFontSize(12);
          doc.text(name, xPosition, 20, {
            align: 'left'
          });
          const widthMm = img.width * 25.4 / pdfSpec.dpi;
          const heightMm = img.height * 25.4 / pdfSpec.dpi;
          doc.addImage(legendUrl, 'PNG', xPosition, 23, widthMm, heightMm);
          xPosition += widthMm + 20;
        } catch (error) {
          Logger.error('Error while creating a legend: ', error);
        }
      }
    }

    dispatch(setIsLoading(false));

    return doc;
  };

  const getAttributionsText = (pdfSpec: any) => {
    const attributions = pdfSpec.layers
      .filter((layer: any) => !!layer.attribution)
      .map((layer: any) => layer.attribution);

    return attributions
      .filter((attribution: string, pos: number) => attributions.indexOf(attribution) === pos)
      .join(', ') || 'Unknown source';
  };

  return (
    <RGPrintButton
      tooltip={t('PrintButton.tooltip')}
      tooltipProps={{
        mouseEnterDelay: 0.5
      }}
      className="export-button"
      outputFileName="wzb-webkarte"
      onProgressChange={onProgressChange}
      title={t('PrintButton.pdfTitle')}
      legendTitle={t('PrintButton.pdfLegendTitle')}
      format='pdf'
      northArrow={map?.getView().getRotation() !== 0}
      scaleBar={true}
      icon={
        <FontAwesomeIcon
          icon={faFilePdf}
        />
      }
      pdfPrintFunc={pdfPrintFunc}
      mapSize={[190, 247, 'mm']}
      dpi={254}
      {...passThroughProps}
    />
  );
};

export default PrintButton;
